'use client';
import { PropertyDetails, TrustSignal } from '@interfaces/expedia/property';
import { v2Links } from '@utils/navigation/links';
import { Building05, Button, ChevronLeft, Typography } from '@v2/ui';
import Link from 'next/link';
import pluralize from 'pluralize';
import { useEffect, useMemo, useRef } from 'react';

import { simpleRenderToString } from '@v2/utils/simple-render-to-string';
import type { Map as HotelMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import { useCurrencyContext } from '@contexts/currency-context';
import { useBuildPackageBundle } from '@hooks/use-build-package-bundle';
import { useBuildPackagesStore } from '@store/build-package-store';
import { applyMargin } from '@utils/currency';
import { formatCurrencyV2 } from '@utils/format-currency-v2';
import { buildHotelGa4Item } from '@v2/analytics/ecommerce';
import { trackGa4ItemView } from '@v2/analytics/ga4';
import { StarRatings } from '@v2/components/StarRatings';
import { cn } from '@v2/utils';
import {
  GuestRatingBadge,
  TrustSignalsList,
} from '@v2/views/SearchHotel/components/TrustSignals';
import { HOTEL_AMENETIES } from '@v2/views/SearchHotel/components/data';
import { useGetHotels } from '@v2/views/SearchHotel/hooks/use-hotels-list';
import { differenceInCalendarDays } from 'date-fns';
import format from 'date-fns/format';
import moment from 'moment-timezone';
import { useSearchParams } from 'next/navigation';
import { useMediaQuery } from 'usehooks-ts';
import { HotelDetailsDialog } from './HotelDetailsDialog';
import { HotelImagesDialog, HotelMobileImages } from './HotelImagesDialog';
const apiKey = process.env.MAPS_API_KEY;
const region = 'us-east-1';
const mapName = 'explore.map';

type Props = {
  propertyDetails: PropertyDetails;
};

const mapStyle = {
  background: 'var(--ui-background-booking)',
};

export function HotelOverview({ propertyDetails }: Props) {
  const {
    name,
    descriptions,
    rating,
    amenities,
    image,
    images,
    minimumTotalPrice,
  } = propertyDetails;

  const { data: hotels, lowestHotelPrice } = useGetHotels();

  const matchingSearchHotel = hotels?.find(
    (h) => String(h.id) === String(propertyDetails.id)
  );

  const minPriceInHotels = useMemo(() => {
    let min = Infinity;
    let id: number | null = null;
    for (const h of hotels ?? []) {
      if (
        typeof h.price === 'number' &&
        Number.isFinite(h.price) &&
        h.price > 0
      ) {
        if (h.price < min) {
          min = h.price;
          id = h.id;
        }
      }
    }
    return id;
  }, [hotels]);

  const isCheapest = matchingSearchHotel
    ? matchingSearchHotel.id === minPriceInHotels
    : false;

  const rawSignals = propertyDetails.trustSignals?.length
    ? propertyDetails.trustSignals
    : matchingSearchHotel?.trustSignals;

  const trustSignals = useMemo(() => {
    const list: TrustSignal[] = rawSignals ? [...rawSignals] : [];
    if (isCheapest && !list.includes('cheapest')) {
      const withoutBestPrice = list.filter((s) => s !== 'best_price');
      return ['cheapest' as TrustSignal, ...withoutBestPrice];
    }
    return list;
  }, [rawSignals, isCheapest]);

  const effectiveGuestRating =
    propertyDetails.guestRating ??
    propertyDetails.guestRate ??
    matchingSearchHotel?.guestRating ??
    matchingSearchHotel?.guestRate;

  const effectiveGuestRatingCount =
    propertyDetails.guestRatingCount ??
    propertyDetails.guestRateCount ??
    matchingSearchHotel?.guestRatingCount ??
    matchingSearchHotel?.guestRateCount;

  const { endDate, nights, startDate, rooomsLength } = useBuildPackagesStore(
    (state) => ({
      startDate: moment(state.hotelStartDate).tz('GMT').toDate(),
      endDate: moment(state.hotelEndDate).tz('GMT').toDate(),
      nights: differenceInCalendarDays(
        moment(state.hotelEndDate).tz('GMT').toDate(),
        moment(state.hotelStartDate).tz('GMT').toDate()
      ),

      rooomsLength: state.roomConfigurations.length,
    })
  );

  const { priceRate, currency } = useCurrencyContext();

  const handleScrollToRooms = () => {
    const targetElement = document.getElementById('roomsSection');
    if (targetElement) {
      const topOffset = targetElement.offsetTop;
      window.scrollTo({
        top: topOffset,
        behavior: 'smooth',
      });
    }
  };

  const { packageGuests, isBundle } = useBuildPackageBundle();
  const safePackageGuests = Math.max(1, packageGuests || 0);

  const lowestPerGuest = lowestHotelPrice / safePackageGuests;
  const packagePrice = Math.abs(
    lowestPerGuest - minimumTotalPrice / safePackageGuests
  );
  const perTravelerPrice = !isBundle
    ? minimumTotalPrice / safePackageGuests
    : packagePrice;
  const trackedHotelId = useRef<string>();

  useEffect(() => {
    const hotelId = String(propertyDetails.id || '');
    if (!hotelId || trackedHotelId.current === hotelId) return;

    trackedHotelId.current = hotelId;
    const price = Number(applyMargin(perTravelerPrice, priceRate, currency));
    trackGa4ItemView({
      currency,
      value: price,
      item: buildHotelGa4Item({
        hotelId,
        hotelName: name,
        price,
      }),
    });
  }, [currency, name, perTravelerPrice, priceRate, propertyDetails.id]);

  const searchParams = useSearchParams();

  const hotelStartDate = moment(startDate).tz('GMT').toDate();
  const hotelEndDate = moment(endDate).tz('GMT').toDate();
  const isTablet = useMediaQuery('(min-width: 768px)');
  const visibleAmenities = HOTEL_AMENETIES.filter((item) =>
    amenities?.toLowerCase().includes(item.value.toLowerCase())
  );

  return (
    <div className="w-full rounded-xl border border-gray-200 bg-white md:mt-5">
      <div className="flex items-center justify-between border-b border-gray-200 p-4 md:p-8">
        <Link
          href={{
            pathname: v2Links.searchHotel,
            query: {
              eventId: searchParams?.get('eventId'),
            },
          }}
          className="flex items-center"
        >
          <ChevronLeft
            size="20"
            pathProps={{ stroke: '#667085' }}
            className="mr-2"
          />
          <Typography size={isTablet ? 'tmd' : 'tsm'} className="text-gray-500">
            All properties
          </Typography>
        </Link>

        <Button onClick={handleScrollToRooms} size={isTablet ? 'md' : 'lg'}>
          View rooms
        </Button>
      </div>

      <div className="flex flex-col-reverse md:flex-col">
        <div className="flex flex-col items-center justify-between space-y-8 px-4 py-6 md:flex-row md:p-8">
          <div className="flex flex-col items-start justify-start gap-y-5 md:gap-y-6">
            <div>
              {trustSignals.length > 0 && (
                <TrustSignalsList
                  signals={trustSignals}
                  size="md"
                  className="mb-3"
                />
              )}
              <h2 className="mb-3 text-2xl font-bold md:text-4xl md:tracking-[-0.72px]">
                {name}
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <StarRatings active={rating} count={5} />
                {effectiveGuestRating != null && effectiveGuestRating > 0 && (
                  <>
                    <span className="text-gray-300" aria-hidden="true">
                      ·
                    </span>
                    <GuestRatingBadge
                      rating={effectiveGuestRating}
                      count={effectiveGuestRatingCount}
                      size="md"
                    />
                  </>
                )}
              </div>
            </div>
            <p className="text-xs font-medium first-line:max-w-[68ch] md:text-sm">
              {descriptions[0]}
            </p>
            <div className="mb-3 flex w-[90%] flex-wrap items-center justify-start gap-x-5 gap-y-3">
              {visibleAmenities.map((item) => (
                <div className="flex items-center gap-1.5" key={item.label}>
                  {item.icon}
                  <Typography
                    size={isTablet ? 'tsm' : 'txs'}
                    className="font-medium text-gray-500"
                  >
                    {item.label}
                  </Typography>
                </div>
              ))}
            </div>
            <HotelDetailsDialog {...propertyDetails} />
          </div>
          <HotelMapLocation {...propertyDetails} />
        </div>
        <HotelMainImages mainImage={image} images={images} />
      </div>

      <div className="flex flex-col items-start justify-between space-y-6 border-t border-gray-200 px-4 py-6 md:flex-row md:items-center md:p-8">
        <div className="flex flex-col gap-y-1.5">
          <Typography size="tlg" className="font-bold">
            Price details
          </Typography>
          <p className="text-xs font-medium md:text-sm">
            {format(new Date(hotelStartDate), 'MMM d, yyyy')} -{' '}
            {format(new Date(hotelEndDate), 'MMM d, yyyy')} ({nights}{' '}
            {pluralize('night', nights)}) <br />
            Includes taxes & fees
          </p>
        </div>

        <div className="flex flex-col items-start gap-y-1.5 md:items-end">
          <Typography size={isTablet ? 'dmd' : 'dsm'} className="font-bold">
            {isBundle ? '+' : ''}
            {formatCurrencyV2(
              perTravelerPrice,
              priceRate,
              currency,
              false,
              false
            )}
          </Typography>
          <p className="text-xs font-medium text-gray-500 md:text-sm">
            Per traveler (includes {rooomsLength}{' '}
            {pluralize('room', rooomsLength)})
          </p>
        </div>
      </div>
    </div>
  );
}

function HotelMapLocation(props: PropertyDetails) {
  const { latitude, longitude, fullAddress } = props;

  const mapContainer = useRef<HTMLDivElement>(null);
  const isTablet = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    const container = mapContainer.current;
    if (!container) return;
    let disposed = false;
    let map: HotelMap | undefined;

    const drawMap = async () => {
      const [{ default: maplibregl }, markerHtml] = await Promise.all([
        import('maplibre-gl'),
        simpleRenderToString(
          <div className="relative rounded-lg bg-white p-2 shadow-md">
            <Building05 />
            <MapArrow />
            <div className="absolute -bottom-[20px] left-0 right-0 shadow-md" />
          </div>
        ),
      ]);
      if (disposed) return;
      map = new maplibregl.Map({
        container,
        style: `https://maps.geo.${region}.amazonaws.com/maps/v0/maps/${mapName}/style-descriptor?key=${apiKey}`,
        center: [longitude, latitude],
        zoom: 11,
      });
      const eventMarker = document.createElement('div');
      eventMarker.innerHTML = markerHtml;
      new maplibregl.Marker({ element: eventMarker })
        .setLngLat([longitude, latitude])
        .addTo(map);
    };

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      void drawMap().catch((error) =>
        console.error('Unable to load hotel map', error)
      );
    });
    observer.observe(container);
    return () => {
      disposed = true;
      observer.disconnect();
      map?.remove();
    };
  }, [latitude, longitude]);

  return (
    <div className="w-full flex-shrink-0 md:w-[420px]">
      <div
        ref={mapContainer}
        style={mapStyle}
        className={cn('h-[180px] w-full rounded-lg')}
      />

      <div className="mt-4">
        <Typography size={isTablet ? 'tmd' : 'tsm'} className="font-medium">
          {fullAddress}
        </Typography>
        <Typography size="tsm" className="mt-2 text-gray-500">
          {/* {distance.toFixed(1)} mi from address */}
        </Typography>
      </div>
    </div>
  );
}

function HotelMainImages(props: { mainImage: string; images: string[] }) {
  const { mainImage, images } = props;
  const isTablet = useMediaQuery('(min-width: 768px)');

  if (!isTablet)
    return <HotelMobileImages mainImage={mainImage} images={images} />;

  return <HotelImagesDialog images={images} />;
}

export function MapArrow() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="9"
      viewBox="0 0 16 9"
      fill="none"
      className="absolute -bottom-[7px] left-1/2 -translate-x-1/2 transform"
    >
      <path
        d="M14.0711 0.485289C14.962 0.485289 15.4081 1.56243 14.7782 2.1924L8.70711 8.26347C8.31658 8.654 7.68342 8.654 7.29289 8.26347L1.22183 2.1924C0.591867 1.56243 1.03803 0.485289 1.92894 0.485289L14.0711 0.485289Z"
        fill="white"
      />
    </svg>
  );
}
